import { useCallback, useEffect, useMemo, useState } from "react";
import { INichePreview, INichePreviewList, IUser } from "@/common/api/services/types";
import { useFeedsStore } from "@/common/store/feedsStore";
import { useGifStore } from "@/common/store/gifStore";
import { useUsersStore } from "@/common/store/storeUsers";
import { useNichesStore } from "@/common/store/storeNiches";
import { useNichePreviewStore } from "@/common/store/nichePreviewStore";

interface IOptions {
  canRunRequest?: boolean;
}

export const usePaginatedNichePreviews = (
  keyParams: (string | number | boolean | undefined)[],
  func: (page?: number) => Promise<INichePreviewList | undefined>,
  { canRunRequest }: IOptions = { canRunRequest: true },
) => {
  const key = keyParams.join(":");

  const feed = useFeedsStore((state) => state.getFeed(key));
  const appendFeed = useFeedsStore((state) => state.appendFeed);

  const updateGifs = useGifStore((state) => state.updateGifs);
  const updateUsers = useUsersStore((state) => state.updateUsers);
  const updateNiches = useNichesStore((state) => state.updateNiches);

  const updatePreviews = useNichePreviewStore((state) => state.updatePreviews);
  const getPreview = useNichePreviewStore((state) => state.getPreview);
  const previewsData = useNichePreviewStore((state) => state.previews);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const { ids = [], page, pages, total } = feed || {};

  const fetchRequest = useCallback(async (page?: number) => {
    try {
      setError(null);
      setLoading(true);

      const res = await func(page);

      if (res) {
        if (!Array.isArray(res.previews)) {
          throw new Error("Niche previews response has no previews list.");
        }

        const previews = res.previews;

        updateGifs(previews.map(preview => preview.gif));
        updateUsers(previews.map(preview => preview.user).filter((user): user is IUser => !!user));
        updateNiches(previews.map(preview => preview.niche));
        updatePreviews(previews);

        appendFeed(key, {
          page: res.page,
          pages: res.pages,
          total: res.total,
          ids: previews.map(preview => preview.niche.id),
        });
      }
    } catch (e) {
      const error = e as Error;
      setError(error);
      console.error("Error reading niche previews.", error);
    } finally {
      setLoading(false);
    }
  }, [func, key]);

  const hasMore = useMemo(
    () => Boolean(page && pages && page < pages && ids.length),
    [page, pages, ids.length],
  );

  const loadMore = useCallback(async () => {
    if (hasMore && page) {
      await fetchRequest(page + 1);
    }
  }, [page, hasMore, fetchRequest]);

  useEffect(() => {
    if (feed === undefined && canRunRequest) {
      fetchRequest();
    }
  }, [feed, fetchRequest, canRunRequest]);

  const previews = useMemo(
    () => ids
      .map(getPreview)
      .filter((preview): preview is INichePreview => !!preview),
    [ids, getPreview, previewsData],
  );

  const isInitialLoading = feed === undefined && canRunRequest && !error;
  const isLoading = loading || isInitialLoading;

  return {
    previews,
    total: total || null,
    loading: !!isLoading,
    error,
    hasMore,
    loadMore,
  };
};
